import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import api, { getBaseURL } from '../services/api';
import {
  LuPencil,
  LuTrash2,
  LuCheckCircle,
  LuXCircle,
  LuBarChart3,
  LuPieChart,
  LuLineChart,
  LuChevronDown,
  LuChevronUp,
  LuClipboardList,
  LuUsers,
  LuDollarSign,
  LuFlaskConical,
  LuUser,
  LuSearch,
  LuCalendarDays,
  LuLightbulb,
  LuTrophy,
  LuMedal,
  LuTag,
  LuBadge,
  LuPackage,
  LuGift,
  LuDog,
  LuMegaphone,
  LuTicket,
  LuCreditCard,
} from 'react-icons/lu';

const iconStyle = { display: 'inline-block', verticalAlign: 'middle', marginRight: '6px' };

const TestTubeIcon = () => <LuFlaskConical size={16} style={iconStyle} />;
const UserIcon = () => <LuUser size={16} style={iconStyle} />;
const SearchIcon = () => <LuSearch size={16} style={iconStyle} />;
const CalendarIcon = () => <LuCalendarDays size={16} style={iconStyle} />;
const LightbulbIcon = () => <LuLightbulb size={16} style={iconStyle} />;
const TrophyIcon = () => <LuTrophy size={18} style={iconStyle} />;
const MedalIcon = ({ color }) => <LuMedal size={18} style={{ ...iconStyle, color }} />;
const TagIcon = () => <LuTag size={16} style={iconStyle} />;
const IdCardIcon = () => <LuBadge size={16} style={iconStyle} />;
const PackageIcon = () => <LuPackage size={16} style={iconStyle} />;
const GiftIcon = () => <LuGift size={16} style={iconStyle} />;
const CheckCircleIcon = () => <LuCheckCircle size={16} style={iconStyle} />;
const PawIcon = () => <LuDog size={18} style={iconStyle} />;
const MegaphoneIcon = () => <LuMegaphone size={18} style={iconStyle} />;
const TicketIcon = () => <LuTicket size={18} style={iconStyle} />;
const CreditCardIcon = () => <LuCreditCard size={18} style={iconStyle} />;
const ClipboardListIcon = () => <LuClipboardList size={18} style={iconStyle} />;
const BarChart3Icon = () => <LuBarChart3 size={18} style={iconStyle} />;
import { 
  BarChart, Bar, PieChart, Pie, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell 
} from 'recharts';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5f7fa;
`;

const Header = styled.header`
  background: ${props => props.theme.colors.gradientPrimary};
  color: ${props => props.theme.colors.white};
  padding: 32px 40px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15);
`;

const HeaderWrapper = styled.div`
  max-width: 1320px;
  margin: 0 auto;
  width: 100%;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 24px;
`;

const HeaderBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  min-width: 260px;
`;

const HeaderLogo = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.2);

  img {
    width: 70px;
    height: 70px;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    width: 72px;
    height: 72px;

    img {
      width: 52px;
      height: 52px;
    }
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  color: ${props => props.theme.colors.white};

  span {
    font-size: 13px;
    letter-spacing: 0.3em;
    text-transform: uppercase;
    opacity: 0.8;
    margin-bottom: 4px;
  }

  strong {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
  }

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  color: ${props => props.theme.colors.white};
`;

const UserInfo = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.white};
  text-align: right;
  line-height: 1.3;

  span {
    display: block;
    font-size: 12px;
    opacity: 0.8;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    font-size: 16px;
    font-weight: 600;
  }
`;

const LogoutButton = styled.button`
  padding: 10px 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: ${props => props.theme.colors.white};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.white};
`;

const NavContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding: 0 16px;
`;

const Nav = styled.nav`
  display: flex;
  gap: 8px;
  background: rgba(255, 255, 255, 0.08);
  padding: 6px;
  border-radius: 12px;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  
  /* Scrollbar minimalista para mobile */
  &::-webkit-scrollbar {
    height: 2px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    gap: 4px;
    padding: 4px;
    justify-content: flex-start;
  }
`;

const NavLink = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.white};
  font-weight: 500;
  font-size: 14px;
  opacity: ${props => props.$active ? 1 : 0.65};
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 10px 20px;
  border-radius: 8px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: ${props => props.theme.colors.white};
    font-size: 16px;
    flex-shrink: 0;
  }
  
  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.12);
  }
  
  ${props => props.$active && `
    background: rgba(255, 255, 255, 0.20);
    font-weight: 600;
    opacity: 1;
  `}
  
  @media (max-width: 768px) {
    padding: 8px 14px;
    font-size: 13px;
    
    /* Ocultar ícones em mobile */
    svg {
      display: none;
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 12px;
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  background: ${props => {
    if (props.$variant === 'danger') return props.theme?.colors?.error || '#ef4444';
    if (props.$variant === 'warning') return props.theme?.colors?.warning || '#f59e0b';
    if (props.$variant === 'secondary') return '#6b7280';
    return props.theme?.colors?.primary || '#3b82f6';
  }};
  color: #ffffff !important;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  background: ${props => props.$variant === 'danger' ? '#ef4444' : '#3b82f6'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const IconButton = styled.button`
  padding: 8px 10px;
  background: ${props => {
    if (props.$variant === 'danger') return '#ef4444';
    if (props.$variant === 'success') return '#10b981';
    if (props.$variant === 'warning') return '#f59e0b';
    return '#3b82f6';
  }};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    background: ${props => {
      if (props.$variant === 'danger') return '#dc2626';
      if (props.$variant === 'success') return '#059669';
      if (props.$variant === 'warning') return '#d97706';
      return '#2563eb';
    }};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 40px auto;
  padding: 0 40px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => props.color || props.theme.colors.primary};
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.grayMedium};
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${props => props.theme.colors.grayLight};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const CardContent = styled.div`
  padding: 24px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  background: ${props => props.theme.colors.grayLight};
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
  text-transform: uppercase;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.colors.grayLight};
  font-size: 14px;
  color: ${props => props.theme.colors.grayDark};
`;

const Badge = styled.span`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    if (props.status === 'ativo') return '#d1fae5';
    if (props.status === 'encerrado') return '#fef3c7';
    if (props.status === 'vencido') return '#fee2e2';
    if (props.status === 'inativo') return '#fee2e2';
    return props.theme.colors.grayLight;
  }};
  color: ${props => {
    if (props.status === 'ativo') return '#065f46';
    if (props.status === 'encerrado') return '#92400e';
    if (props.status === 'vencido') return '#991b1b';
    if (props.status === 'inativo') return '#991b1b';
    return props.theme.colors.grayDark;
  }};
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  margin-bottom: 24px;
  
  h3 {
    font-size: 20px;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 8px 0;
  }
  
  p {
    font-size: 14px;
    color: ${props => props.theme.colors.grayMedium};
    margin: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid ${props => props.theme.colors.grayLight};
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid ${props => props.theme.colors.grayLight};
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${props => props.theme.colors.grayMedium};
`;

const FilterContainer = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${props => props.theme.colors.grayLight};
  background: #f9fafb;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
`;

const FilterButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.$active ? props.theme.colors.primary : 'white'};
  color: ${props => props.$active ? 'white' : props.theme.colors.grayDark};
  border: 2px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.grayLight};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary : '#f9fafb'};
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: 24px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
  flex: 1;
`;

const MetricCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => props.color || '#3b82f6'};
  
  h4 {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 8px 0;
    font-weight: 500;
  }
  
  p {
    font-size: 28px;
    font-weight: 700;
    color: ${props => props.color || '#3b82f6'};
    margin: 0;
  }
  
  span {
    font-size: 12px;
    color: #9ca3af;
    display: block;
    margin-top: 4px;
  }
`;

const AnalyticsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  thead {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
  
  th {
    padding: 16px;
    text-align: left;
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
  }
  
  td {
    padding: 14px 16px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 14px;
  }
  
  tbody tr:hover {
    background: #f9fafb;
  }
  
  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  
  div {
    height: 100%;
    background: ${props => props.color || '#3b82f6'};
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  border-radius: 4px;
  
  &:hover {
    background: rgba(59, 130, 246, 0.1);
  }
  
  svg {
    font-size: 12px;
  }
`;

const ExpandedRow = styled.tr`
  background: #f9fafb;
  
  td {
    padding: 0 !important;
  }
`;

const ExpandedContent = styled.div`
  padding: 20px 40px;
  animation: slideDown 0.3s ease;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ExpandedTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.grayDark};
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UsageList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const UsageItem = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
  }
`;

const UsageField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  label {
    font-size: 11px;
    font-weight: 600;
    color: ${props => props.theme.colors.grayMedium};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  span {
    font-size: 14px;
    color: ${props => props.theme.colors.grayDark};
    font-weight: 500;
    word-break: break-word;
  }
`;

const EmptyUsage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.colors.grayMedium};
  font-size: 14px;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 16px;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e5e7eb;
    border-top-color: ${props => props.theme.colors.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  span {
    color: ${props => props.theme.colors.grayMedium};
    font-size: 14px;
  }
`;

const FullScreenLoading = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  gap: 20px;
  
  .spinner {
    width: 60px;
    height: 60px;
    border: 5px solid #e5e7eb;
    border-top-color: ${props => props.theme.colors.primary};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  .loading-text {
    color: ${props => props.theme.colors.grayDark};
    font-size: 16px;
    font-weight: 600;
    margin-top: 8px;
  }
`;

const UsageSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-bottom: 16px;
  
  div {
    text-align: center;
    color: white;
    
    label {
      font-size: 11px;
      opacity: 0.9;
      display: block;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    span {
      font-size: 20px;
      font-weight: 700;
      display: block;
    }
  }
`;

const ClickableText = styled.span`
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.7;
  }
`;

const FilterBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: ${props => props.theme.colors.primary};
  color: white;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 16px;
  
  button {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const AdminCupons = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determinar aba ativa baseado na rota
  const activeTab = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/admin/vendedores')) return 'vendedores';
    if (path.includes('/admin/vendas')) return 'vendas';
    if (path.includes('/admin/relatorios')) return 'relatorios';
    return 'cupons'; // default
  }, [location.pathname]);
  
  // Função para navegar entre abas
  const setActiveTab = (tab) => {
    navigate(`/admin/${tab}`);
  };
  
  const [stats, setStats] = useState(null);
  const [cupons, setCupons] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [parentescos, setParentescos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCupom, setSelectedCupom] = useState(null);
  const [selectedCadastro, setSelectedCadastro] = useState(null);
  const [showCadastroModal, setShowCadastroModal] = useState(false);
  const [filtroStatusVendedor, setFiltroStatusVendedor] = useState('ambos');
  const [filtroUtmProvider, setFiltroUtmProvider] = useState('');
  
  // Estados para Relatórios - Iniciar com mês vigente
  const [filtroDataInicio, setFiltroDataInicio] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0]; // YYYY-MM-DD
  });
  const [filtroDataFim, setFiltroDataFim] = useState(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return lastDay.toISOString().split('T')[0]; // YYYY-MM-DD
  });
  const [filtroVendedorRelatorio, setFiltroVendedorRelatorio] = useState('todos');
  const [filtroStatusRelatorio, setFiltroStatusRelatorio] = useState('todos');
  const [expandedVendedores, setExpandedVendedores] = useState(new Set());
  const [usosDetalhados, setUsosDetalhados] = useState({});
  
  // Estados para Vendas
  const [cadastros, setCadastros] = useState([]);
  
  // Inicializar filtros de vendas com o mês vigente
  const [filtroDataInicioVendas, setFiltroDataInicioVendas] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0]; // YYYY-MM-DD
  });
  const [filtroDataFimVendas, setFiltroDataFimVendas] = useState(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return lastDay.toISOString().split('T')[0]; // YYYY-MM-DD
  });
  const [filtroVendedorVendas, setFiltroVendedorVendas] = useState('todos');
  const [incluirTestes, setIncluirTestes] = useState(false); // Por padrão não inclui testes
  const [tipoAgrupamentoEvolucao, setTipoAgrupamentoEvolucao] = useState('mes'); // 'mes' ou 'dia'
  const [formData, setFormData] = useState({
    codigo: '',
    vendedorId: '',
    beneficioTipo: 'percentual',
    beneficioValor: '',
    descontoTaxaAdesaoAplica: false,
    descontoTaxaAdesaoTipo: 'percentual',
    descontoTaxaAdesaoValor: '',
    quantidadeTotal: '',
    validade: ''
  });

  // URL base unificada, usando a mesma lógica do serviço de API
  const API_URL = getBaseURL();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_access_token');
    return {
      Authorization: `Bearer ${token}`
    };
  };

  const checkAuth = () => {
    const token = localStorage.getItem('admin_access_token');
    if (!token) {
      navigate('/admin/login');
      return false;
    }
    return true;
  };

  const fetchData = async () => {
    if (!checkAuth()) return;

    try {
      setLoading(true);
      
      const [statsRes, cuponsRes, vendedoresRes, cadastrosRes, parentescosRes] = await Promise.all([
        axios.get(`${API_URL}/cupons/stats/dashboard`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/cupons`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/cupons/vendedores/list`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/cliente/cadastros`, { headers: getAuthHeaders() })
          .catch((error) => {
            console.error('Erro ao carregar cadastros:', error);
            return { data: { cadastros: [] } };
          }),
        axios.get(`${API_URL}/parentescos`)
          .catch((error) => {
            console.error('Erro ao carregar parentescos:', error);
            return { data: { success: false, data: [] } };
          })
      ]);

      setStats(statsRes.data);
      setCupons(cuponsRes.data.cupons || []);
      setVendedores(vendedoresRes.data.vendedores || []);
      setCadastros(cadastrosRes.data.cadastros || []);

      // Carregar parentescos
      if (parentescosRes.data.success && parentescosRes.data.data) {
        setParentescos(parentescosRes.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Sessão expirada. Faça login novamente.');
        localStorage.clear();
        navigate('/admin/login');
      } else {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Buscar cupons usados quando acessar a aba de relatórios

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logout realizado com sucesso');
    navigate('/admin/login');
  };

  const handleCreateCupom = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        codigo: formData.codigo,
        vendedorId: formData.vendedorId,
        beneficio: {
          tipo: formData.beneficioTipo,
          valor: parseFloat(formData.beneficioValor)
        },
        quantidadeTotal: parseInt(formData.quantidadeTotal),
        validade: formData.validade || null
      };

      // Adicionar desconto na taxa de adesão se aplicável
      if (formData.descontoTaxaAdesaoAplica && formData.descontoTaxaAdesaoValor) {
        payload.descontoTaxaAdesao = {
          aplica: true,
          tipo: formData.descontoTaxaAdesaoTipo,
          valor: parseFloat(formData.descontoTaxaAdesaoValor)
        };
      }

      await axios.post(`${API_URL}/cupons`, payload, { headers: getAuthHeaders() });

      toast.success('Cupom criado com sucesso!');
      setShowModal(false);
      setFormData({
        codigo: '',
        vendedorId: '',
        beneficioTipo: 'percentual',
        beneficioValor: '',
        descontoTaxaAdesaoAplica: false,
        descontoTaxaAdesaoTipo: 'percentual',
        descontoTaxaAdesaoValor: '',
        quantidadeTotal: '',
        validade: ''
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao criar cupom:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar cupom');
    }
  };


  const handleEditCupom = (cupom) => {
    setSelectedCupom(cupom);
    setFormData({
      codigo: cupom.codigo,
      vendedorId: cupom.vendedorId,
      beneficioTipo: cupom.beneficio.tipo,
      beneficioValor: cupom.beneficio.valor.toString(),
      descontoTaxaAdesaoAplica: cupom.descontoTaxaAdesao?.aplica || false,
      descontoTaxaAdesaoTipo: cupom.descontoTaxaAdesao?.tipo || 'percentual',
      descontoTaxaAdesaoValor: cupom.descontoTaxaAdesao?.valor?.toString() || '',
      quantidadeTotal: cupom.quantidadeTotal.toString(),
      validade: cupom.validade ? new Date(cupom.validade).toISOString().split('T')[0] : ''
    });
    setShowEditModal(true);
  };

  const handleUpdateCupom = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        vendedorId: formData.vendedorId,
        beneficio: {
          tipo: formData.beneficioTipo,
          valor: parseFloat(formData.beneficioValor)
        },
        quantidadeTotal: parseInt(formData.quantidadeTotal),
        validade: formData.validade || null
      };

      // Adicionar desconto na taxa de adesão
      if (formData.descontoTaxaAdesaoAplica && formData.descontoTaxaAdesaoValor) {
        payload.descontoTaxaAdesao = {
          aplica: true,
          tipo: formData.descontoTaxaAdesaoTipo,
          valor: parseFloat(formData.descontoTaxaAdesaoValor)
        };
      } else {
        payload.descontoTaxaAdesao = {
          aplica: false,
          tipo: 'percentual',
          valor: 0
        };
      }

      await axios.patch(`${API_URL}/cupons/${selectedCupom.id}`, payload, { headers: getAuthHeaders() });

      toast.success('Cupom atualizado com sucesso!');
      setShowEditModal(false);
      setSelectedCupom(null);
      setFormData({
        codigo: '',
        vendedorId: '',
        beneficioTipo: 'percentual',
        beneficioValor: '',
        descontoTaxaAdesaoAplica: false,
        descontoTaxaAdesaoTipo: 'percentual',
        descontoTaxaAdesaoValor: '',
        quantidadeTotal: '',
        validade: ''
      });
      fetchData();
    } catch (error) {
      console.error('Erro ao atualizar cupom:', error);
      toast.error(error.response?.data?.message || 'Erro ao atualizar cupom');
    }
  };

  const handleDeleteCupom = (cupom) => {
    setSelectedCupom(cupom);
    setShowDeleteModal(true);
  };

  const confirmDeleteCupom = async () => {
    try {
      await axios.delete(`${API_URL}/cupons/${selectedCupom.id}`, { 
        headers: getAuthHeaders() 
      });

      toast.success('Cupom excluído com sucesso!');
      setShowDeleteModal(false);
      setSelectedCupom(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao excluir cupom:', error);
      toast.error(error.response?.data?.message || 'Erro ao excluir cupom');
    }
  };


  const handleToggleStatusCupom = async (cupom) => {
    try {
      // Verifica o status atual do cupom (campo status, não o status calculado)
      const statusAtual = cupom.status || 'ativo';
      const novoStatus = statusAtual === 'ativo' ? 'inativo' : 'ativo';
      
      await axios.patch(`${API_URL}/cupons/${cupom.id}`, {
        status: novoStatus
      }, { headers: getAuthHeaders() });

      toast.success(`Cupom ${novoStatus === 'ativo' ? 'ativado' : 'inativado'} com sucesso!`);
      fetchData();
    } catch (error) {
      console.error('Erro ao alterar status do cupom:', error);
      toast.error(error.response?.data?.message || 'Erro ao alterar status do cupom');
    }
  };


  const userInfo = JSON.parse(localStorage.getItem('admin_user') || '{}');

  // Função para filtrar vendedores por status
  const getVendedoresFiltrados = () => {
    if (filtroStatusVendedor === 'ambos') {
      return vendedores;
    }
    return vendedores.filter(v => (v.status || 'ativo') === filtroStatusVendedor);
  };

  // Funções para Relatórios
  const getCuponsFiltrados = () => {
    let cuponsFiltrados = [...cupons];

    // Filtro por vendedor
    if (filtroVendedorRelatorio !== 'todos') {
      cuponsFiltrados = cuponsFiltrados.filter(c => c.vendedorId === filtroVendedorRelatorio);
    }

    // Filtro por status
    if (filtroStatusRelatorio !== 'todos') {
      cuponsFiltrados = cuponsFiltrados.filter(c => getStatusCupom(c) === filtroStatusRelatorio);
    }

    // Filtro por data (baseado na data de criação ou validade)
    if (filtroDataInicio) {
      const dataInicio = new Date(filtroDataInicio);
      cuponsFiltrados = cuponsFiltrados.filter(c => {
        if (c.criadoEm) {
          return new Date(c.criadoEm) >= dataInicio;
        }
        return true;
      });
    }

    if (filtroDataFim) {
      const dataFim = new Date(filtroDataFim);
      dataFim.setHours(23, 59, 59, 999);
      cuponsFiltrados = cuponsFiltrados.filter(c => {
        if (c.criadoEm) {
          return new Date(c.criadoEm) <= dataFim;
        }
        return true;
      });
    }

    return cuponsFiltrados;
  };

  const getDesempenhoPorVendedor = () => {
    const cuponsFiltrados = getCuponsFiltrados();
    const desempenho = {};

    cuponsFiltrados.forEach(cupom => {
      const vendedorNome = cupom.vendedorNome || 'Sem vendedor';
      if (!desempenho[vendedorNome]) {
        desempenho[vendedorNome] = {
          nome: vendedorNome,
          totalCupons: 0,
          totalUsados: 0,
          totalDisponiveis: 0,
          taxaUso: 0
        };
      }
      
      desempenho[vendedorNome].totalCupons += 1;
      desempenho[vendedorNome].totalUsados += cupom.quantidadeUsada || 0;
      desempenho[vendedorNome].totalDisponiveis += cupom.quantidadeDisponivel || 0;
    });

    // Calcular taxa de uso
    Object.keys(desempenho).forEach(nome => {
      const total = desempenho[nome].totalUsados + desempenho[nome].totalDisponiveis;
      desempenho[nome].taxaUso = total > 0 
        ? ((desempenho[nome].totalUsados / total) * 100).toFixed(1)
        : 0;
    });

    return Object.values(desempenho).sort((a, b) => b.totalUsados - a.totalUsados);
  };

  const getDistribuicaoStatus = () => {
    const cuponsFiltrados = getCuponsFiltrados();
    const distribuicao = {
      ativo: 0,
      encerrado: 0,
      vencido: 0,
      inativo: 0
    };

    cuponsFiltrados.forEach(cupom => {
      const status = getStatusCupom(cupom);
      distribuicao[status] = (distribuicao[status] || 0) + 1;
    });

    return [
      { name: 'Ativos', value: distribuicao.ativo, color: '#10b981' },
      { name: 'Encerrados', value: distribuicao.encerrado, color: '#f59e0b' },
      { name: 'Vencidos', value: distribuicao.vencido, color: '#ef4444' },
      { name: 'Inativos', value: distribuicao.inativo, color: '#6b7280' }
    ].filter(item => item.value > 0);
  };

  const getTopVendedores = (limit = 5) => {
    const desempenho = getDesempenhoPorVendedor();
    return desempenho.slice(0, limit);
  };

  const getMetricasGerais = () => {
    const cuponsFiltrados = getCuponsFiltrados();
    
    const totalUsados = cuponsFiltrados.reduce((acc, cupom) => acc + (cupom.quantidadeUsada || 0), 0);
    const totalDisponiveis = cuponsFiltrados.reduce((acc, cupom) => acc + (cupom.quantidadeDisponivel || 0), 0);
    const totalCupons = cuponsFiltrados.length;
    const totalCapacidade = totalUsados + totalDisponiveis;
    const taxaConversao = totalCapacidade > 0 ? ((totalUsados / totalCapacidade) * 100).toFixed(1) : 0;

    return {
      totalCupons,
      totalUsados,
      totalDisponiveis,
      taxaConversao
    };
  };

  // Função para buscar usos detalhados de um vendedor
  const buscarUsosDetalhados = async (vendedorId, vendedorNome) => {
    try {
      // Buscar histórico de uso dos cupons do vendedor
      const response = await axios.get(`${API_URL}/cupons/vendedores/${vendedorId}/usos`, { 
        headers: getAuthHeaders() 
      });
      
      setUsosDetalhados(prev => ({
        ...prev,
        [vendedorNome]: response.data.usos || []
      }));
    } catch (error) {
      console.error('Erro ao buscar usos detalhados:', error);
      // Se o endpoint não existir, tentar construir dados a partir dos cupons
      const cuponsFiltrados = getCuponsFiltrados().filter(c => c.vendedorId === vendedorId);
      const usosMock = [];
      
      cuponsFiltrados.forEach(cupom => {
        if (cupom.quantidadeUsada > 0) {
          // Criar entradas mock para visualização
          for (let i = 0; i < Math.min(cupom.quantidadeUsada, 5); i++) {
            usosMock.push({
              codigo: cupom.codigo,
              data: cupom.criadoEm || new Date().toISOString(),
              cliente: 'Cliente não disponível',
              plano: 'Informação não disponível',
              valor: 0
            });
          }
        }
      });
      
      setUsosDetalhados(prev => ({
        ...prev,
        [vendedorNome]: usosMock
      }));
    }
  };

  // Função para expandir/recolher linha da tabela
  const toggleExpandVendedor = (vendedorNome, vendedorId) => {
    const newExpanded = new Set(expandedVendedores);
    
    if (newExpanded.has(vendedorNome)) {
      newExpanded.delete(vendedorNome);
    } else {
      newExpanded.add(vendedorNome);
      // Buscar dados se ainda não foram buscados
      if (!usosDetalhados[vendedorNome]) {
        buscarUsosDetalhados(vendedorId, vendedorNome);
      }
    }
    
    setExpandedVendedores(newExpanded);
  };

  // Função para aplicar filtro ao clicar no gráfico
  const handleChartClick = (data, type) => {
    if (type === 'vendedor' && data && data.nome) {
      const vendedor = vendedores.find(v => v.nome === data.nome);
      if (vendedor) {
        setFiltroVendedorRelatorio(vendedor.id);
        toast.info(`Filtrado por vendedor: ${data.nome}`);
      }
    } else if (type === 'status' && data && data.name) {
      const statusMap = {
        'Ativos': 'ativo',
        'Encerrados': 'encerrado',
        'Vencidos': 'vencido',
        'Inativos': 'inativo'
      };
      const status = statusMap[data.name];
      if (status) {
        setFiltroStatusRelatorio(status);
        toast.info(`Filtrado por status: ${data.name}`);
      }
    }
  };

  // Função para calcular o status real do cupom
  const getStatusCupom = (cupom) => {
    // Se o cupom está inativo, retorna inativo
    if (cupom.status === 'inativo') {
      return 'inativo';
    }
    
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0); // Normaliza para início do dia
    
    // Se tem validade definida
    if (cupom.validade) {
      const dataValidade = new Date(cupom.validade);
      dataValidade.setHours(0, 0, 0, 0);
      
      // Se está vencido (data atual > data de validade)
      if (hoje > dataValidade) {
        return 'vencido';
      }
    }
    
    // Se está no período de validade (ou sem validade definida)
    // Verifica a quantidade disponível
    if (cupom.quantidadeDisponivel === 0) {
      return 'encerrado';
    }
    
    return 'ativo';
  };

  // Função para verificar se é cadastro de teste
  const isCadastroTeste = (cadastro) => {
    const email = cadastro.titularEmail || cadastro.titular?.email || '';
    const emailLower = email.toLowerCase();
    return emailLower === 'cleytongomesx@gmail.com' || emailLower.includes('@santacasacopacabana');
  };

  // Funções para Dashboard de Vendas
  // Função para filtrar cadastros (apenas CONFIRMADOS para cards e rankings)
  const getCadastrosFiltrados = () => {
    let cadastrosFiltrados = [...cadastros];

    // Filtro: apenas cadastros CONFIRMADOS (para rankings e cards)
    cadastrosFiltrados = cadastrosFiltrados.filter(c => c.status === 'CONFIRMADO');

    // Filtro para excluir/incluir testes
    if (!incluirTestes) {
      cadastrosFiltrados = cadastrosFiltrados.filter(c => !isCadastroTeste(c));
    }

    // Filtro por vendedor
    if (filtroVendedorVendas !== 'todos') {
      cadastrosFiltrados = cadastrosFiltrados.filter(c => c.vendedorId === filtroVendedorVendas);
    }

    // Filtro por UTM Provider (filtro local)
    if (filtroUtmProvider && filtroUtmProvider.trim() !== '') {
      const termo = filtroUtmProvider.trim().toLowerCase();
      cadastrosFiltrados = cadastrosFiltrados.filter(c => {
        const provider = (c.utm_provider || '').toLowerCase();
        return provider.includes(termo);
      });
    }

    // Filtro por data
    if (filtroDataInicioVendas) {
      const dataInicio = new Date(filtroDataInicioVendas);
      cadastrosFiltrados = cadastrosFiltrados.filter(c => {
        if (c.dataCadastro || c.createdAt) {
          return new Date(c.dataCadastro || c.createdAt) >= dataInicio;
        }
        return true;
      });
    }

    if (filtroDataFimVendas) {
      const dataFim = new Date(filtroDataFimVendas);
      dataFim.setHours(23, 59, 59, 999);
      cadastrosFiltrados = cadastrosFiltrados.filter(c => {
        if (c.dataCadastro || c.createdAt) {
          return new Date(c.dataCadastro || c.createdAt) <= dataFim;
        }
        return true;
      });
    }

    return cadastrosFiltrados;
  };

  // Função para filtrar cadastros para a tabela (todos os status)
  const getCadastrosFiltradosParaTabela = () => {
    let cadastrosFiltrados = [...cadastros];

    // Filtro para excluir/incluir testes
    if (!incluirTestes) {
      cadastrosFiltrados = cadastrosFiltrados.filter(c => !isCadastroTeste(c));
    }

    // Filtro por vendedor
    if (filtroVendedorVendas !== 'todos') {
      cadastrosFiltrados = cadastrosFiltrados.filter(c => c.vendedorId === filtroVendedorVendas);
    }

    // Filtro por UTM Provider (filtro local)
    if (filtroUtmProvider && filtroUtmProvider.trim() !== '') {
      const termo = filtroUtmProvider.trim().toLowerCase();
      cadastrosFiltrados = cadastrosFiltrados.filter(c => {
        const provider = (c.utm_provider || '').toLowerCase();
        return provider.includes(termo);
      });
    }

    // Filtro por data
    if (filtroDataInicioVendas) {
      const dataInicio = new Date(filtroDataInicioVendas);
      cadastrosFiltrados = cadastrosFiltrados.filter(c => {
        if (c.dataCadastro || c.createdAt) {
          return new Date(c.dataCadastro || c.createdAt) >= dataInicio;
        }
        return true;
      });
    }

    if (filtroDataFimVendas) {
      const dataFim = new Date(filtroDataFimVendas);
      dataFim.setHours(23, 59, 59, 999);
      cadastrosFiltrados = cadastrosFiltrados.filter(c => {
        if (c.dataCadastro || c.createdAt) {
          return new Date(c.dataCadastro || c.createdAt) <= dataFim;
        }
        return true;
      });
    }

    return cadastrosFiltrados;
  };

  const getKPIsVendas = () => {
    const cadastrosFiltrados = getCadastrosFiltrados();
    
    const totalCadastros = cadastrosFiltrados.length;
    
    // Receita Total: apenas cadastros com status PAGO
    const totalVendas = cadastrosFiltrados
      .filter(c => {
        const status = (c.statusPagamento || '-').toUpperCase();
        return status === 'PAGO';
      })
      .reduce((acc, c) => {
        const valor = parseFloat(c.valorTotal || c.valor || c.valorPlano || 0);
        return acc + valor;
      }, 0);
    
    // Valor Pendente: todos os outros status (não PAGO)
    const totalPendente = cadastrosFiltrados
      .filter(c => {
        const status = (c.statusPagamento || '-').toUpperCase();
        return status !== 'PAGO' && status !== '-';
      })
      .reduce((acc, c) => {
        const valor = parseFloat(c.valorTotal || c.valor || c.valorPlano || 0);
        return acc + valor;
      }, 0);

    // DESABILITADO - Taxa de adesão
    // const totalTaxasAdesao = cadastrosFiltrados.reduce((acc, c) => {
    //   const taxa = parseFloat(c.taxaAdesao || 0);
    //   return acc + taxa;
    // }, 0);
    const totalTaxasAdesao = 0;

    const totalDescontos = cadastrosFiltrados.reduce((acc, c) => {
      const desconto = parseFloat(c.descontoAplicado || 0);
      return acc + desconto;
    }, 0);

    // Ticket médio: considerar todos os cadastros (pagos + pendentes)
    const totalGeral = cadastrosFiltrados.reduce((acc, c) => {
      const valor = parseFloat(c.valorTotal || c.valor || c.valorPlano || 0);
      return acc + valor;
    }, 0);
    const ticketMedio = totalCadastros > 0 ? totalGeral / totalCadastros : 0;

    const cadastrosComCupom = cadastrosFiltrados.filter(c => c.cupomCodigo || c.cupom).length;
    const taxaUsoCupom = totalCadastros > 0 ? (cadastrosComCupom / totalCadastros) * 100 : 0;

    // Calcular total de vidas (pessoas) e pets
    const totalVidas = cadastrosFiltrados.reduce((acc, c) => {
      const pessoas = parseInt(c.pessoasTotal || 0);
      return acc + pessoas;
    }, 0);

    const totalPets = cadastrosFiltrados.reduce((acc, c) => {
      const pets = parseInt(c.petsTotal || 0);
      return acc + pets;
    }, 0);

    return {
      totalCadastros,
      totalVendas,
      totalPendente,
      totalTaxasAdesao,
      totalDescontos,
      ticketMedio,
      cadastrosComCupom,
      taxaUsoCupom,
      totalVidas,
      totalPets
    };
  };

  const getRankingPlanos = () => {
    const cadastrosFiltrados = getCadastrosFiltrados();
    const planos = {};

    cadastrosFiltrados.forEach(cadastro => {
      const planoNome = cadastro.planoNome || cadastro.plano || 'Sem plano';
      const valor = parseFloat(cadastro.valorTotal || cadastro.valor || cadastro.valorPlano || 0);

      if (!planos[planoNome]) {
        planos[planoNome] = {
          nome: planoNome,
          quantidade: 0,
          valorTotal: 0
        };
      }

      planos[planoNome].quantidade += 1;
      planos[planoNome].valorTotal += valor;
    });

    return Object.values(planos).sort((a, b) => b.quantidade - a.quantidade);
  };

  const getVendasPorVendedor = () => {
    const cadastrosFiltrados = getCadastrosFiltrados();
    const vendas = {};

    cadastrosFiltrados.forEach(cadastro => {
      const vendedorNome = cadastro.vendedorNome || 'Sem vendedor';
      const vendedorId = cadastro.vendedorId;
      const valor = parseFloat(cadastro.valorTotal || cadastro.valor || cadastro.valorPlano || 0);

      if (!vendas[vendedorNome]) {
        vendas[vendedorNome] = {
          nome: vendedorNome,
          id: vendedorId,
          quantidade: 0,
          valorTotal: 0
        };
      }

      vendas[vendedorNome].quantidade += 1;
      vendas[vendedorNome].valorTotal += valor;
    });

    return Object.values(vendas).sort((a, b) => b.valorTotal - a.valorTotal);
  };

  const getVendasPorPeriodo = () => {
    const cadastrosFiltrados = getCadastrosFiltrados();
    const periodos = {};

    cadastrosFiltrados.forEach(cadastro => {
      const data = new Date(cadastro.dataCadastro || cadastro.createdAt);
      const valor = parseFloat(cadastro.valorTotal || cadastro.valor || cadastro.valorPlano || 0);

      let periodoKey, periodoLabel, dataReferencia;

      if (tipoAgrupamentoEvolucao === 'dia') {
        // Agrupar por dia
        periodoKey = data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        periodoLabel = data.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
        dataReferencia = new Date(data.getFullYear(), data.getMonth(), data.getDate());
      } else {
        // Agrupar por mês (padrão)
        periodoKey = data.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
        periodoLabel = periodoKey;
        dataReferencia = new Date(data.getFullYear(), data.getMonth(), 1);
      }

      if (!periodos[periodoKey]) {
        periodos[periodoKey] = {
          periodo: periodoLabel,
          quantidade: 0,
          valor: 0,
          dataReferencia: dataReferencia
        };
      }

      periodos[periodoKey].quantidade += 1;
      periodos[periodoKey].valor += valor;
    });

    // Ordenar do mais antigo para o mais novo
    return Object.values(periodos).sort((a, b) => a.dataReferencia - b.dataReferencia);
  };

  // Função para aplicar filtro ao clicar nos gráficos da aba Vendas
  const handleChartClickVendas = (data, type) => {
    if (type === 'vendedor' && data && data.nome) {
      const vendedor = vendedores.find(v => v.nome === data.nome);
      if (vendedor) {
        setFiltroVendedorVendas(vendedor.id);
        toast.info(`Filtrado por vendedor: ${data.nome}`);
      }
    } else if (type === 'plano' && data && data.nome) {
      // Para filtrar por plano, podemos mostrar um toast informativo
      // já que não temos um filtro de plano implementado
      toast.info(`Plano selecionado: ${data.nome}`);
    }
  };

  // Função para formatar o texto do status
  const getStatusTexto = (status) => {
    const textos = {
      'ativo': 'ativo',
      'encerrado': 'encerrado',
      'vencido': 'vencido',
      'inativo': 'inativo'
    };
    return textos[status] || status;
  };

  if (loading) {
    return (
      <FullScreenLoading>
        <div className="spinner"></div>
        <div className="loading-text">Carregando...</div>
      </FullScreenLoading>
    );
  }

  return (
    <PageContainer>
      <Header>
        <HeaderWrapper>
          <HeaderContent>
            <HeaderBrand>
              <HeaderLogo>
                <img src="/logoscc.png" alt="Santa Casa Copacabana" />
              </HeaderLogo>
              <HeaderTitle>
                <span>Portal de Contratação</span>
                <strong>Santa Casa Copacabana</strong>
              </HeaderTitle>
            </HeaderBrand>
            <HeaderActions>
              <UserInfo>
                <span>Bem-vindo</span>
                <strong>{userInfo?.nome || 'Administrador'}</strong>
              </UserInfo>
              <LogoutButton onClick={handleLogout}>
                Sair
              </LogoutButton>
            </HeaderActions>
          </HeaderContent>
          <NavContainer>
            <Nav>
              <NavLink 
                $active={activeTab === 'cupons'} 
                onClick={() => setActiveTab('cupons')}
              >
                <LuClipboardList />
                Cupons
              </NavLink>
              <NavLink 
                $active={activeTab === 'vendedores'} 
                onClick={() => setActiveTab('vendedores')}
              >
                <LuUsers />
                Vendedores
              </NavLink>
              <NavLink 
                $active={activeTab === 'vendas'} 
                onClick={() => setActiveTab('vendas')}
              >
                <LuDollarSign />
                Vendas
              </NavLink>
              <NavLink 
                $active={activeTab === 'relatorios'} 
                onClick={() => setActiveTab('relatorios')}
              >
                <LuBarChart3 />
                Relatórios
              </NavLink>
            </Nav>
          </NavContainer>
        </HeaderWrapper>
      </Header>

      <Content>
        {activeTab === 'vendas' ? (
          <>
            {/* Filtros de Vendas */}
            <FilterRow>
              <FilterField>
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={filtroDataInicioVendas}
                  onChange={(e) => setFiltroDataInicioVendas(e.target.value)}
                />
              </FilterField>
              
              <FilterField>
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={filtroDataFimVendas}
                  onChange={(e) => setFiltroDataFimVendas(e.target.value)}
                />
              </FilterField>
              
              <FilterField>
                <Label>Vendedor</Label>
                <Select
                  value={filtroVendedorVendas}
                  onChange={(e) => setFiltroVendedorVendas(e.target.value)}
                >
                  <option value="todos">Todos os Vendedores</option>
                  {vendedores.map(v => (
                    <option key={v.id} value={v.id}>{v.nome}</option>
                  ))}
                </Select>
              </FilterField>

              <FilterField>
                <Label>Anunciante</Label>
                <Input
                  type="text"
                  placeholder="Filtrar por anunciante"
                  value={filtroUtmProvider}
                  onChange={(e) => setFiltroUtmProvider(e.target.value)}
                />
              </FilterField>
              
              <FilterField style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 'auto' }}>
                <input
                  type="checkbox"
                  id="incluirTestes"
                  checked={incluirTestes}
                  onChange={(e) => setIncluirTestes(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <Label htmlFor="incluirTestes" style={{ marginBottom: 0, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  Incluir Testes
                </Label>
              </FilterField>
              
              <Button 
                onClick={() => {
                  setFiltroDataInicioVendas('');
                  setFiltroDataFimVendas('');
                  setFiltroVendedorVendas('todos');
                  setFiltroUtmProvider('');
                  setIncluirTestes(false);
                }}
                $variant="secondary"
              >
                Limpar Filtros
              </Button>
            </FilterRow>

            {/* Badges de Filtros Ativos */}
            {(filtroVendedorVendas !== 'todos' || filtroDataInicioVendas || filtroDataFimVendas || incluirTestes || (filtroUtmProvider && filtroUtmProvider.trim() !== '')) && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                {incluirTestes && (
                  <FilterBadge style={{ background: '#fef3c7', color: '#92400e' }}>
                    <TestTubeIcon />Incluindo cadastros de teste
                    <span onClick={() => setIncluirTestes(false)}>×</span>
                  </FilterBadge>
                )}
                
                {filtroVendedorVendas !== 'todos' && (
                  <FilterBadge>
                    <UserIcon />Vendedor: {vendedores.find(v => v.id === filtroVendedorVendas)?.nome || 'N/A'}
                    <button onClick={() => setFiltroVendedorVendas('todos')}>×</button>
                  </FilterBadge>
                )}

                {filtroUtmProvider && filtroUtmProvider.trim() !== '' && (
                  <FilterBadge>
                    <SearchIcon />Anunciante: {filtroUtmProvider}
                    <button onClick={() => setFiltroUtmProvider('')}>×</button>
                  </FilterBadge>
                )}
                
                {(filtroDataInicioVendas || filtroDataFimVendas) && (
                  <FilterBadge>
                    <CalendarIcon />Período: {filtroDataInicioVendas ? new Date(filtroDataInicioVendas).toLocaleDateString('pt-BR') : '...'} até {filtroDataFimVendas ? new Date(filtroDataFimVendas).toLocaleDateString('pt-BR') : '...'}
                    <button onClick={() => { setFiltroDataInicioVendas(''); setFiltroDataFimVendas(''); }}>×</button>
                  </FilterBadge>
                )}
              </div>
            )}

            {/* KPIs Principais */}
            <StatsGrid>
              <MetricCard color="#3b82f6">
                <h4>Total de Cadastros</h4>
                <p>{getKPIsVendas().totalCadastros}</p>
                <span>Cadastros realizados</span>
              </MetricCard>
              
              <MetricCard color="#10b981">
                <h4>Receita Total</h4>
                <p>R$ {getKPIsVendas().totalVendas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <span>Valores pagos</span>
              </MetricCard>
              
              <MetricCard color="#f59e0b">
                <h4>Receita Pendente</h4>
                <p>R$ {getKPIsVendas().totalPendente.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <span>Valores pendentes</span>
              </MetricCard>
              
              <MetricCard color="#8b5cf6">
                <h4>Ticket Médio</h4>
                <p>R$ {getKPIsVendas().ticketMedio.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <span>Por contrato</span>
              </MetricCard>
              
              {/* DESABILITADO - Taxa de adesão */}
              {/* <MetricCard color="#f59e0b">
                <h4>Taxa de Adesão Total</h4>
                <p>R$ {getKPIsVendas().totalTaxasAdesao.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <span>Receita em taxas</span>
              </MetricCard> */}
              
              <MetricCard color="#ef4444">
                <h4>Total em Descontos</h4>
                <p>R$ {getKPIsVendas().totalDescontos.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <span>Valor total de descontos</span>
              </MetricCard>
              
              <MetricCard color="#06b6d4">
                <h4>Uso de Cupons</h4>
                <p>{getKPIsVendas().taxaUsoCupom.toFixed(1)}%</p>
                <span>{getKPIsVendas().cadastrosComCupom} com cupom</span>
              </MetricCard>
              
              <MetricCard color="#ec4899">
                <h4>Total de Vidas</h4>
                <p>{getKPIsVendas().totalVidas}</p>
                <span>Titulares + Dependentes</span>
              </MetricCard>
              
              <MetricCard color="#14b8a6">
                <h4>Total de PETs</h4>
                <p>{getKPIsVendas().totalPets}</p>
                <span>Animais de estimação</span>
              </MetricCard>
            </StatsGrid>

            {/* Gráficos de Vendas */}
            <ChartsGrid>
              {/* Ranking de Planos */}
              <ChartCard>
                <ChartTitle>
                  <LuBarChart3 /> Ranking de Planos Mais Vendidos
                </ChartTitle>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  <LightbulbIcon />Clique em uma barra para ver detalhes do plano
                </p>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={getRankingPlanos().slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nome" angle={-45} textAnchor="end" height={120} />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'Valor Total (R$)') {
                          return [`R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, name];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="quantidade" 
                      fill="#3b82f6" 
                      name="Quantidade"
                      onClick={(data) => handleChartClickVendas(data, 'plano')}
                      cursor="pointer"
                    />
                    <Bar 
                      dataKey="valorTotal" 
                      fill="#10b981" 
                      name="Valor Total (R$)"
                      onClick={(data) => handleChartClickVendas(data, 'plano')}
                      cursor="pointer"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Vendas por Vendedor */}
              <ChartCard>
                <ChartTitle>
                  <LuPieChart /> Distribuição de Vendas por Vendedor
                </ChartTitle>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  <LightbulbIcon />Clique em uma fatia para filtrar por vendedor
                </p>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={getVendasPorVendedor()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ nome, percent }) => `${nome.substring(0, 15)}... ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="quantidade"
                      onClick={(data) => handleChartClickVendas(data, 'vendedor')}
                      cursor="pointer"
                    >
                      {getVendasPorVendedor().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'][index % 6]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </ChartsGrid>

            {/* Evolução de Vendas por Período */}
            {getVendasPorPeriodo().length > 0 && (
              <ChartCard>
                <ChartTitle>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span>
                      <LuLineChart /> Evolução de Vendas por Período
                    </span>
                    <FilterButtonGroup style={{ margin: 0 }}>
                      <FilterButton 
                        $active={tipoAgrupamentoEvolucao === 'mes'}
                        onClick={() => setTipoAgrupamentoEvolucao('mes')}
                      >
                        Mês
                      </FilterButton>
                      <FilterButton 
                        $active={tipoAgrupamentoEvolucao === 'dia'}
                        onClick={() => setTipoAgrupamentoEvolucao('dia')}
                      >
                        Dia
                      </FilterButton>
                    </FilterButtonGroup>
                  </div>
                </ChartTitle>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={getVendasPorPeriodo()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="periodo" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'Valor (R$)') {
                          return [`R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, name];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Line yAxisId="left" type="monotone" dataKey="quantidade" stroke="#3b82f6" name="Quantidade" strokeWidth={2} />
                    <Line yAxisId="right" type="monotone" dataKey="valor" stroke="#10b981" name="Valor (R$)" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* Tabela de Ranking de Vendedores */}
            <Card>
              <CardHeader>
                <CardTitle><TrophyIcon />Ranking de Vendedores</CardTitle>
              </CardHeader>
              <CardContent>
                {getVendasPorVendedor().length === 0 ? (
                  <EmptyState>
                    Nenhuma venda registrada no período selecionado.
                  </EmptyState>
                ) : (
                  <AnalyticsTable>
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>Posição</th>
                        <th>Vendedor</th>
                        <th>Quantidade</th>
                        <th>Valor Total</th>
                        <th>Ticket Médio</th>
                        <th>Performance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getVendasPorVendedor().map((vendedor, index) => {
                        const ticketMedio = vendedor.quantidade > 0 ? vendedor.valorTotal / vendedor.quantidade : 0;
                        const maxValor = Math.max(...getVendasPorVendedor().map(v => v.valorTotal));
                        const performance = maxValor > 0 ? (vendedor.valorTotal / maxValor) * 100 : 0;
                        
                        return (
                          <tr key={index}>
                            <td style={{ textAlign: 'center', fontSize: '20px', fontWeight: 'bold' }}>
                              {index === 0 && <MedalIcon color="#d4af37" />}
                              {index === 1 && <MedalIcon color="#a8a9ad" />}
                              {index === 2 && <MedalIcon color="#ad8a56" />}
                              {index > 2 && `${index + 1}º`}
                            </td>
                            <td><strong>{vendedor.nome}</strong></td>
                            <td style={{ color: '#3b82f6', fontWeight: '600' }}>
                              {vendedor.quantidade}
                            </td>
                            <td style={{ color: '#10b981', fontWeight: '600' }}>
                              R$ {vendedor.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </td>
                            <td>
                              R$ {ticketMedio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </td>
                            <td>
                              <ProgressBar color={
                                index === 0 ? '#f59e0b' :
                                index === 1 ? '#9ca3af' :
                                index === 2 ? '#cd7f32' : '#3b82f6'
                              }>
                                <div style={{ width: `${performance}%` }} />
                              </ProgressBar>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </AnalyticsTable>
                )}
              </CardContent>
            </Card>

            {/* Tabela de Ranking de Planos */}
            <Card style={{ marginTop: '20px' }}>
              <CardHeader>
                <CardTitle><BarChart3Icon />Ranking de Planos</CardTitle>
              </CardHeader>
              <CardContent>
                {getRankingPlanos().length === 0 ? (
                  <EmptyState>
                    Nenhum plano vendido no período selecionado.
                  </EmptyState>
                ) : (
                  <AnalyticsTable>
                    <thead>
                      <tr>
                        <th style={{ width: '60px' }}>Posição</th>
                        <th>Plano</th>
                        <th>Quantidade Vendida</th>
                        <th>Valor Total</th>
                        <th>Valor Médio</th>
                        <th>Participação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getRankingPlanos().map((plano, index) => {
                        const valorMedio = plano.quantidade > 0 ? plano.valorTotal / plano.quantidade : 0;
                        const totalGeral = getRankingPlanos().reduce((acc, p) => acc + p.quantidade, 0);
                        const participacao = totalGeral > 0 ? (plano.quantidade / totalGeral) * 100 : 0;
                        
                        return (
                          <tr key={index}>
                            <td style={{ textAlign: 'center', fontSize: '18px', fontWeight: 'bold' }}>
                              {index + 1}º
                            </td>
                            <td><strong>{plano.nome}</strong></td>
                            <td style={{ color: '#3b82f6', fontWeight: '600' }}>
                              {plano.quantidade}
                            </td>
                            <td style={{ color: '#10b981', fontWeight: '600' }}>
                              R$ {plano.valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </td>
                            <td>
                              R$ {valorMedio.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontWeight: '600', color: '#8b5cf6', minWidth: '50px' }}>
                                  {participacao.toFixed(1)}%
                                </span>
                                <ProgressBar color="#8b5cf6">
                                  <div style={{ width: `${participacao}%` }} />
                                </ProgressBar>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </AnalyticsTable>
                )}
              </CardContent>
            </Card>

            {/* Últimos Cadastros */}
            <Card style={{ marginTop: '20px' }}>
              <CardHeader>
                <CardTitle><ClipboardListIcon />Últimos Cadastros Realizados</CardTitle>
                <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 'normal' }}>
                  Total: {getCadastrosFiltradosParaTabela().length} cadastros
                </span>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <LoadingSpinner>
                    <div className="spinner" />
                    <span>Carregando cadastros...</span>
                  </LoadingSpinner>
                ) : getCadastrosFiltradosParaTabela().length === 0 ? (
                  <EmptyState>
                    {cadastros.length === 0 
                      ? 'Nenhum cadastro disponível no sistema.'
                      : 'Nenhum cadastro encontrado com os filtros selecionados.'
                    }
                  </EmptyState>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>Data</Th>
                        <Th>Cliente</Th>
                        <Th>Plano</Th>
                        <Th>Vendedor</Th>
                        <Th>Anunciante</Th>
                        <Th>Status</Th>
                        <Th>Pagamento</Th>
                        <Th>Vidas</Th>
                        <Th>PETs</Th>
                        <Th>Valor</Th>
                        {/* DESABILITADO - Taxa de adesão */}
                        {/* <Th>Taxa Adesão</Th> */}
                        <Th>Cupom</Th>
                        <Th>Desconto</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {getCadastrosFiltradosParaTabela()
                        .sort((a, b) => {
                          const dataA = new Date(a.dataCadastro || a.createdAt);
                          const dataB = new Date(b.dataCadastro || b.createdAt);
                          return dataB - dataA;
                        })
                        .slice(0, 50)
                        .map((cadastro, index) => {
                          const totalPessoas = parseInt(cadastro.pessoasTotal || 0);
                          const totalPets = parseInt(cadastro.petsTotal || 0);
                          const dependentes = parseInt(cadastro.dependentes || 0);
                          
                          return (
                            <tr key={index}>
                              <Td>
                                {new Date(cadastro.dataCadastro || cadastro.createdAt).toLocaleDateString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric'
                                })}
                              </Td>
                              <Td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <strong 
                                    style={{ 
                                      color: '#3b82f6', 
                                      cursor: 'pointer',
                                      textDecoration: 'underline'
                                    }}
                                    onClick={() => {
                                      setSelectedCadastro(cadastro);
                                      setShowCadastroModal(true);
                                    }}
                                  >
                                    {cadastro.titularNome || cadastro.nomeCliente || cadastro.nome || 'N/A'}
                                  </strong>
                                  {isCadastroTeste(cadastro) && (
                                    <span style={{
                                      background: '#fbbf24',
                                      color: '#78350f',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      fontSize: '10px',
                                      fontWeight: '700',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px'
                                    }}>
                                      TESTE
                                    </span>
                                  )}
                                </div>
                              </Td>
                              <Td>{cadastro.planoNome || cadastro.plano || 'N/A'}</Td>
                              <Td>{cadastro.vendedorNome || 'Sem vendedor'}</Td>
                              <Td>{cadastro.utm_provider || '-'}</Td>
                              <Td>
                                <Badge status={cadastro.status === 'CONFIRMADO' ? 'ativo' : cadastro.status === 'AGUARDANDO_CONFIRMACAO' ? 'warning' : 'inativo'}>
                                  {cadastro.status || 'N/A'}
                                </Badge>
                              </Td>
                              <Td>
                                {(() => {
                                  const status = (cadastro.statusPagamento || '-').toUpperCase();
                                  let color = '#9ca3af'; // Cinza para '-'
                                  let bgColor = '#f3f4f6';
                                  
                                  if (status === 'PAGO') {
                                    color = '#065f46';
                                    bgColor = '#d1fae5';
                                  } else if (status === 'VENCIDO') {
                                    color = '#991b1b';
                                    bgColor = '#fee2e2';
                                  } else if (status === 'ABERTO' || status === 'PENDENTE') {
                                    color = '#92400e';
                                    bgColor = '#fef3c7';
                                  }
                                  
                                  // Converter "ABERTO" para "EM ABERTO" na exibição
                                  let textoExibido = cadastro.statusPagamento || '-';
                                  if (status === 'ABERTO') {
                                    textoExibido = 'EM_ABERTO';
                                  }
                                  
                                  return (
                                    <Badge 
                                      status={
                                        status === 'PAGO' ? 'ativo' :
                                        status === 'VENCIDO' ? 'inativo' :
                                        status === 'ABERTO' || status === 'PENDENTE' ? 'warning' :
                                        null
                                      }
                                      style={{
                                        backgroundColor: bgColor,
                                        color: color,
                                        border: 'none'
                                      }}
                                    >
                                      {textoExibido}
                                    </Badge>
                                  );
                                })()}
                              </Td>
                              <Td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                                    {totalPessoas} {totalPessoas === 1 ? 'vida' : 'vidas'}
                                  </span>
                                  {dependentes > 0 && (
                                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                      (1 tit. + {dependentes} dep.)
                                    </span>
                                  )}
                                  {totalPessoas === 1 && dependentes === 0 && (
                                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                                      (somente titular)
                                    </span>
                                  )}
                                </div>
                              </Td>
                              <Td style={{ color: '#14b8a6', fontWeight: '600' }}>
                                {totalPets > 0 ? totalPets : '-'}
                              </Td>
                              <Td style={{ color: '#10b981', fontWeight: '600' }}>
                                R$ {(parseFloat(cadastro.valorTotal || cadastro.valor || cadastro.valorPlano || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </Td>
                              {/* DESABILITADO - Taxa de adesão */}
                              {/* <Td>
                                R$ {(parseFloat(cadastro.taxaAdesao || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </Td> */}
                              <Td>
                                {cadastro.cupomCodigo || cadastro.cupom ? (
                                  <Badge status="ativo">{cadastro.cupomCodigo || cadastro.cupom}</Badge>
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>-</span>
                                )}
                              </Td>
                              <Td style={{ color: '#f59e0b', fontWeight: '600' }}>
                                {cadastro.descontoAplicado ? (
                                  `R$ ${parseFloat(cadastro.descontoAplicado).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>-</span>
                                )}
                              </Td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        ) : activeTab === 'relatorios' ? (
          <>
            {/* Filtros de Relatório */}
            <FilterRow>
              <FilterField>
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={filtroDataInicio}
                  onChange={(e) => setFiltroDataInicio(e.target.value)}
                />
              </FilterField>
              
              <FilterField>
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={filtroDataFim}
                  onChange={(e) => setFiltroDataFim(e.target.value)}
                />
              </FilterField>
              
              <FilterField>
                <Label>Vendedor</Label>
                <Select
                  value={filtroVendedorRelatorio}
                  onChange={(e) => setFiltroVendedorRelatorio(e.target.value)}
                >
                  <option value="todos">Todos os Vendedores</option>
                  {vendedores.map(v => (
                    <option key={v.id} value={v.id}>{v.nome}</option>
                  ))}
                </Select>
              </FilterField>
              
              <FilterField>
                <Label>Status</Label>
                <Select
                  value={filtroStatusRelatorio}
                  onChange={(e) => setFiltroStatusRelatorio(e.target.value)}
                >
                  <option value="todos">Todos os Status</option>
                  <option value="ativo">Ativos</option>
                  <option value="encerrado">Encerrados</option>
                  <option value="vencido">Vencidos</option>
                  <option value="inativo">Inativos</option>
                </Select>
              </FilterField>
              
              <Button 
                onClick={() => {
                  setFiltroDataInicio('');
                  setFiltroDataFim('');
                  setFiltroVendedorRelatorio('todos');
                  setFiltroStatusRelatorio('todos');
                }}
                $variant="secondary"
              >
                Limpar Filtros
              </Button>
            </FilterRow>

            {/* Badges de Filtros Ativos */}
            {(filtroVendedorRelatorio !== 'todos' || filtroStatusRelatorio !== 'todos' || filtroDataInicio || filtroDataFim) && (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
                {filtroVendedorRelatorio !== 'todos' && (
                  <FilterBadge>
                    <UserIcon />Vendedor: {vendedores.find(v => v.id === filtroVendedorRelatorio)?.nome || 'N/A'}
                    <button onClick={() => setFiltroVendedorRelatorio('todos')}>×</button>
                  </FilterBadge>
                )}
                
                {filtroStatusRelatorio !== 'todos' && (
                  <FilterBadge>
                    <TagIcon />Status: {filtroStatusRelatorio.charAt(0).toUpperCase() + filtroStatusRelatorio.slice(1)}
                    <button onClick={() => setFiltroStatusRelatorio('todos')}>×</button>
                  </FilterBadge>
                )}
                
                {(filtroDataInicio || filtroDataFim) && (
                  <FilterBadge>
                    <CalendarIcon />Período: {filtroDataInicio ? new Date(filtroDataInicio).toLocaleDateString('pt-BR') : '...'} até {filtroDataFim ? new Date(filtroDataFim).toLocaleDateString('pt-BR') : '...'}
                    <button onClick={() => { setFiltroDataInicio(''); setFiltroDataFim(''); }}>×</button>
                  </FilterBadge>
                )}
              </div>
            )}

            {/* Métricas Principais */}
            <StatsGrid>
              <MetricCard color="#3b82f6">
                <h4>Total de Cupons</h4>
                <p>{getMetricasGerais().totalCupons}</p>
                <span>Cupons filtrados</span>
              </MetricCard>
              
              <MetricCard color="#10b981">
                <h4>Total Utilizado</h4>
                <p>{getMetricasGerais().totalUsados}</p>
                <span>Usos realizados</span>
              </MetricCard>
              
              <MetricCard color="#f59e0b">
                <h4>Disponíveis</h4>
                <p>{getMetricasGerais().totalDisponiveis}</p>
                <span>Ainda não utilizados</span>
              </MetricCard>
              
              <MetricCard color="#8b5cf6">
                <h4>Taxa de Conversão</h4>
                <p>{getMetricasGerais().taxaConversao}%</p>
                <span>Cupons utilizados</span>
              </MetricCard>
            </StatsGrid>

            {/* Gráficos */}
            <ChartsGrid>
              {/* Gráfico de Desempenho por Vendedor */}
              <ChartCard>
                <ChartTitle>
                  <LuBarChart3 /> Desempenho por Vendedor
                </ChartTitle>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  <LightbulbIcon />Clique em uma barra para filtrar por vendedor
                </p>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getDesempenhoPorVendedor()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nome" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                    <Legend />
                    <Bar 
                      dataKey="totalUsados" 
                      fill="#10b981" 
                      name="Usados"
                      onClick={(data) => handleChartClick(data, 'vendedor')}
                      cursor="pointer"
                    />
                    <Bar 
                      dataKey="totalDisponiveis" 
                      fill="#3b82f6" 
                      name="Disponíveis"
                      onClick={(data) => handleChartClick(data, 'vendedor')}
                      cursor="pointer"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Gráfico de Distribuição de Status */}
              <ChartCard>
                <ChartTitle>
                  <LuPieChart /> Distribuição por Status
                </ChartTitle>
                <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                  <LightbulbIcon />Clique em uma fatia para filtrar por status
                </p>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getDistribuicaoStatus()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      onClick={(data) => handleChartClick(data, 'status')}
                      cursor="pointer"
                    >
                      {getDistribuicaoStatus().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </ChartsGrid>

            {/* Gráfico de Taxa de Conversão por Vendedor */}
            <ChartCard>
              <ChartTitle>
                <LuLineChart /> Taxa de Conversão por Vendedor
              </ChartTitle>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={getDesempenhoPorVendedor()} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" unit="%" />
                  <YAxis dataKey="nome" type="category" width={150} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="taxaUso" fill="#8b5cf6" name="Taxa de Uso (%)" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Tabela Analítica Detalhada */}
            <Card>
              <CardHeader>
                <CardTitle>Análise Detalhada por Vendedor</CardTitle>
              </CardHeader>
              <CardContent>
                {getDesempenhoPorVendedor().length === 0 ? (
                  <EmptyState>
                    Nenhum dado disponível com os filtros selecionados.
                  </EmptyState>
                ) : (
                  <AnalyticsTable>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>Vendedor</th>
                        <th>Total Cupons</th>
                        <th>Usados</th>
                        <th>Disponíveis</th>
                        <th>Taxa de Uso</th>
                        <th>Performance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getDesempenhoPorVendedor().map((vendedor, index) => {
                        const vendedorInfo = vendedores.find(v => v.nome === vendedor.nome);
                        const isExpanded = expandedVendedores.has(vendedor.nome);
                        
                        return (
                          <React.Fragment key={index}>
                            <tr>
                              <td>
                                {vendedor.totalUsados > 0 && (
                                  <ExpandButton 
                                    onClick={() => toggleExpandVendedor(vendedor.nome, vendedorInfo?.id)}
                                    title={isExpanded ? 'Recolher detalhes' : 'Ver detalhes de uso'}
                                  >
                                    {isExpanded ? <LuChevronUp /> : <LuChevronDown />}
                                  </ExpandButton>
                                )}
                              </td>
                              <td><strong>{vendedor.nome}</strong></td>
                              <td>{vendedor.totalCupons}</td>
                              <td style={{ color: '#10b981', fontWeight: '600' }}>
                                {vendedor.totalUsados}
                              </td>
                              <td style={{ color: '#3b82f6', fontWeight: '600' }}>
                                {vendedor.totalDisponiveis}
                              </td>
                              <td>
                                <strong style={{ color: '#8b5cf6' }}>
                                  {vendedor.taxaUso}%
                                </strong>
                              </td>
                              <td>
                                <ProgressBar color={
                                  vendedor.taxaUso >= 70 ? '#10b981' :
                                  vendedor.taxaUso >= 40 ? '#f59e0b' : '#ef4444'
                                }>
                                  <div style={{ width: `${vendedor.taxaUso}%` }} />
                                </ProgressBar>
                              </td>
                            </tr>
                            
                            {isExpanded && (
                              <ExpandedRow>
                                <td colSpan="7">
                                  <ExpandedContent>
                                    <ExpandedTitle>
                                      <ClipboardListIcon />Histórico de Uso dos Cupons
                                    </ExpandedTitle>
                                    
                                    {!usosDetalhados[vendedor.nome] ? (
                                      <LoadingSpinner>
                                        <div className="spinner" />
                                        <span>Carregando histórico de usos...</span>
                                      </LoadingSpinner>
                                    ) : usosDetalhados[vendedor.nome].length === 0 ? (
                                      <EmptyUsage>
                                        Nenhum uso registrado para este vendedor.
                                      </EmptyUsage>
                                    ) : (
                                      <>
                                        <UsageSummary>
                                          <div>
                                            <label>Total de Usos</label>
                                            <span>{usosDetalhados[vendedor.nome].length}</span>
                                          </div>
                                          <div>
                                            <label>Desconto Total</label>
                                            <span>
                                              R$ {usosDetalhados[vendedor.nome]
                                                .reduce((sum, uso) => sum + (Number(uso.descontoAplicado) || 0), 0)
                                                .toFixed(2)}
                                            </span>
                                          </div>
                                          <div>
                                            <label>Valor Total Vendido</label>
                                            <span>
                                              R$ {usosDetalhados[vendedor.nome]
                                                .reduce((sum, uso) => sum + (Number(uso.valorFinal) || 0), 0)
                                                .toFixed(2)}
                                            </span>
                                          </div>
                                        </UsageSummary>
                                        
                                        <UsageList>
                                        {usosDetalhados[vendedor.nome].map((uso, idx) => (
                                          <UsageItem key={idx}>
                                            <UsageField>
                                              <label><TicketIcon />Cupom Utilizado</label>
                                              <span style={{ color: '#3b82f6', fontWeight: '700', fontSize: '15px' }}>
                                                {uso.codigo || 'N/A'}
                                              </span>
                                            </UsageField>
                                            
                                            <UsageField>
                                              <label><CalendarIcon />Data do Cadastro</label>
                                              <span>
                                                {uso.dataUso 
                                                  ? new Date(uso.dataUso).toLocaleDateString('pt-BR', {
                                                      day: '2-digit',
                                                      month: '2-digit',
                                                      year: 'numeric',
                                                      hour: '2-digit',
                                                      minute: '2-digit'
                                                    })
                                                  : uso.data 
                                                    ? new Date(uso.data).toLocaleDateString('pt-BR')
                                                    : 'N/A'
                                                }
                                              </span>
                                            </UsageField>
                                            
                                            <UsageField>
                                              <label><UserIcon />Cliente</label>
                                              <span style={{ fontWeight: '600' }}>
                                                {uso.clienteNome || 'Não disponível'}
                                              </span>
                                            </UsageField>
                                            
                                            <UsageField>
                                              <label><IdCardIcon />CPF</label>
                                              <span>
                                                {uso.clienteCPF || 'Não disponível'}
                                              </span>
                                            </UsageField>
                                            
                                            <UsageField>
                                              <label><PackageIcon />Plano Contratado</label>
                                              <span style={{ fontWeight: '600', color: '#6366f1' }}>
                                                {uso.planoNome || 'Não disponível'}
                                              </span>
                                            </UsageField>
                                            
                                            <UsageField>
                                              <label><LuUsers size={16} style={iconStyle} />Composição</label>
                                              <span>
                                                {uso.pessoasTotal || 1} pessoa(s)
                                                {uso.petsTotal > 0 && ` + ${uso.petsTotal} pet(s)`}
                                              </span>
                                            </UsageField>
                                            
                                            <UsageField>
                                              <label><LuDollarSign size={16} style={iconStyle} />Valor Original</label>
                                              <span style={{ color: '#6b7280', fontWeight: '600' }}>
                                                {uso.valorPlano 
                                                  ? `R$ ${Number(uso.valorPlano).toFixed(2)}`
                                                  : 'R$ 0,00'
                                                }
                                              </span>
                                            </UsageField>
                                            
                                            <UsageField>
                                              <label><GiftIcon />Desconto Aplicado</label>
                                              <span style={{ color: '#ef4444', fontWeight: '700', fontSize: '15px' }}>
                                                {uso.descontoAplicado 
                                                  ? `- R$ ${Number(uso.descontoAplicado).toFixed(2)}`
                                                  : 'R$ 0,00'
                                                }
                                              </span>
                                              {uso.beneficioTipo && (
                                                <span style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                                                  ({uso.beneficioTipo === 'percentual' 
                                                    ? `${uso.beneficioValor}% de desconto` 
                                                    : `R$ ${uso.beneficioValor} fixo`})
                                                </span>
                                              )}
                                            </UsageField>
                                            
                                            <UsageField>
                                              <label><CheckCircleIcon />Valor Final</label>
                                              <span style={{ color: '#10b981', fontWeight: '700', fontSize: '16px' }}>
                                                {uso.valorFinal 
                                                  ? `R$ ${Number(uso.valorFinal).toFixed(2)}`
                                                  : 'R$ 0,00'
                                                }
                                              </span>
                                            </UsageField>
                                          </UsageItem>
                                        ))}
                                      </UsageList>
                                      </>
                                    )}
                                  </ExpandedContent>
                                </td>
                              </ExpandedRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </AnalyticsTable>
                )}
              </CardContent>
            </Card>

            {/* Top 5 Vendedores */}
            <Card style={{ marginTop: '20px' }}>
              <CardHeader>
                <CardTitle><TrophyIcon />Top 5 Vendedores</CardTitle>
              </CardHeader>
              <CardContent>
                <StatsGrid>
                  {getTopVendedores(5).map((vendedor, index) => (
                    <MetricCard 
                      key={index}
                      color={
                        index === 0 ? '#f59e0b' :
                        index === 1 ? '#9ca3af' :
                        index === 2 ? '#cd7f32' : '#3b82f6'
                      }
                    >
                      <h4>
                        {index === 0 && <MedalIcon color="#f59e0b" />}
                        {index === 1 && <MedalIcon color="#9ca3af" />}
                        {index === 2 && <MedalIcon color="#cd7f32" />}
                        {vendedor.nome}
                      </h4>
                      <p>{vendedor.totalUsados}</p>
                      <span>Cupons utilizados • {vendedor.taxaUso}% conversão</span>
                    </MetricCard>
                  ))}
                </StatsGrid>
              </CardContent>
            </Card>

          </>
        ) : activeTab === 'cupons' ? (
          <>
            <StatsGrid>
              <StatCard color="#3b82f6">
                <StatLabel>Total de Cupons</StatLabel>
                <StatValue>{stats?.totalCupons || 0}</StatValue>
              </StatCard>
              <StatCard color="#10b981">
                <StatLabel>Cupons Ativos</StatLabel>
                <StatValue>{stats?.cuponsAtivos || 0}</StatValue>
              </StatCard>
              <StatCard color="#f59e0b">
                <StatLabel>Total Usados</StatLabel>
                <StatValue>{stats?.totalUsados || 0}</StatValue>
              </StatCard>
              <StatCard color="#8b5cf6">
                <StatLabel>Disponíveis</StatLabel>
                <StatValue>{stats?.totalDisponiveis || 0}</StatValue>
              </StatCard>
            </StatsGrid>

            <Card>
              <CardHeader>
                <CardTitle>Cupons Cadastrados</CardTitle>
                <Button onClick={() => setShowModal(true)}>
                  + Novo Cupom
                </Button>
              </CardHeader>
              <CardContent>
                {cupons.length === 0 ? (
                  <EmptyState>
                    Nenhum cupom cadastrado ainda.
                  </EmptyState>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>Código</Th>
                        <Th>Vendedor</Th>
                        <Th>Benefício</Th>
                        <Th>Quantidade</Th>
                        <Th>Disponível</Th>
                        <Th>Validade</Th>
                        <Th>Status</Th>
                        <Th>Ações</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {cupons.map((cupom) => (
                        <tr key={cupom.id}>
                          <Td><strong>{cupom.codigo}</strong></Td>
                          <Td>{cupom.vendedorNome}</Td>
                          <Td>
                            <div>
                              {cupom.beneficio.tipo === 'percentual' 
                                ? `${cupom.beneficio.valor}%`
                                : `R$ ${cupom.beneficio.valor.toFixed(2)}`
                              }
                            </div>
                            {cupom.descontoTaxaAdesao?.aplica && (
                              <div style={{ 
                                fontSize: '11px', 
                                color: '#10b981', 
                                marginTop: '4px',
                                fontWeight: '600'
                              }}>
                                + Taxa: {cupom.descontoTaxaAdesao.tipo === 'percentual' 
                                  ? `${cupom.descontoTaxaAdesao.valor}%`
                                  : `R$ ${cupom.descontoTaxaAdesao.valor.toFixed(2)}`
                                }
                              </div>
                            )}
                          </Td>
                          <Td>{cupom.quantidadeUsada} / {cupom.quantidadeTotal}</Td>
                          <Td>{cupom.quantidadeDisponivel}</Td>
                          <Td>
                            {cupom.validade 
                              ? new Date(cupom.validade).toLocaleDateString('pt-BR')
                              : 'Sem validade'
                            }
                          </Td>
                          <Td>
                            <Badge status={getStatusCupom(cupom)}>
                              {getStatusTexto(getStatusCupom(cupom))}
                            </Badge>
                          </Td>
                          <Td>
                            <ActionButtons>
                              <IconButton 
                                onClick={() => handleEditCupom(cupom)}
                                title="Editar cupom"
                              >
                                <LuPencil />
                              </IconButton>
                              <IconButton 
                                $variant={(cupom.status || 'ativo') === 'ativo' ? 'warning' : 'success'}
                                onClick={() => handleToggleStatusCupom(cupom)}
                                title={(cupom.status || 'ativo') === 'ativo' ? 'Inativar cupom' : 'Ativar cupom'}
                              >
                                {(cupom.status || 'ativo') === 'ativo' ? <LuXCircle /> : <LuCheckCircle />}
                              </IconButton>
                              <IconButton 
                                $variant="danger" 
                                onClick={() => handleDeleteCupom(cupom)}
                                title="Excluir cupom"
                              >
                                <LuTrash2 />
                              </IconButton>
                            </ActionButtons>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <StatsGrid>
              <StatCard color="#3b82f6">
                <StatLabel>Total de Vendedores</StatLabel>
                <StatValue>{vendedores.length}</StatValue>
              </StatCard>
              <StatCard color="#10b981">
                <StatLabel>Vendedores Ativos</StatLabel>
                <StatValue>
                  {vendedores.filter(v => v.status === 'ativo').length}
                </StatValue>
              </StatCard>
            </StatsGrid>

            <Card>
              <CardHeader>
                <CardTitle>Vendedores Cadastrados</CardTitle>
                <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 'normal' }}>
                  Apenas consulta
                </span>
              </CardHeader>
              
              <FilterContainer>
                <FilterGroup>
                  <FilterLabel>Filtrar por status:</FilterLabel>
                  <FilterButtonGroup>
                    <FilterButton 
                      $active={filtroStatusVendedor === 'ativo'}
                      onClick={() => setFiltroStatusVendedor('ativo')}
                    >
                      Ativos
                    </FilterButton>
                    <FilterButton 
                      $active={filtroStatusVendedor === 'inativo'}
                      onClick={() => setFiltroStatusVendedor('inativo')}
                    >
                      Inativos
                    </FilterButton>
                    <FilterButton 
                      $active={filtroStatusVendedor === 'ambos'}
                      onClick={() => setFiltroStatusVendedor('ambos')}
                    >
                      Todos
                    </FilterButton>
                  </FilterButtonGroup>
                </FilterGroup>
              </FilterContainer>

              <CardContent>
                {getVendedoresFiltrados().length === 0 ? (
                  <EmptyState>
                    {filtroStatusVendedor === 'ambos' 
                      ? 'Nenhum vendedor cadastrado ainda.'
                      : `Nenhum vendedor ${filtroStatusVendedor} encontrado.`
                    }
                  </EmptyState>
                ) : (
                  <Table>
                    <thead>
                      <tr>
                        <Th>Nome</Th>
                        <Th>Email</Th>
                        <Th>Telefone</Th>
                        <Th>Status</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {getVendedoresFiltrados().map((vendedor) => (
                        <tr key={vendedor.id}>
                          <Td><strong>{vendedor.nome}</strong></Td>
                          <Td>{vendedor.email}</Td>
                          <Td>{vendedor.telefone || '-'}</Td>
                          <Td>
                            <Badge status={vendedor.status || 'ativo'}>
                              {vendedor.status || 'ativo'}
                            </Badge>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Content>

      {showModal && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Criar Novo Cupom</h3>
              <p>Preencha os dados do cupom de desconto</p>
            </ModalHeader>

            <form onSubmit={handleCreateCupom}>
              <FormGroup>
                <Label>Código do Cupom *</Label>
                <Input
                  type="text"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                  placeholder="Ex: VENDEDOR10"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Vendedor *</Label>
                <Select
                  value={formData.vendedorId}
                  onChange={(e) => setFormData({ ...formData, vendedorId: e.target.value })}
                  required
                >
                  <option value="">Selecione um vendedor</option>
                  {vendedores.map((vendedor) => (
                    <option key={vendedor.id} value={vendedor.id}>
                      {vendedor.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Tipo de Benefício *</Label>
                <Select
                  value={formData.beneficioTipo}
                  onChange={(e) => setFormData({ ...formData, beneficioTipo: e.target.value })}
                  required
                >
                  <option value="percentual">Percentual (%)</option>
                  <option value="fixo">Valor Fixo (R$)</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Valor do Benefício *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.beneficioValor}
                  onChange={(e) => setFormData({ ...formData, beneficioValor: e.target.value })}
                  placeholder={formData.beneficioTipo === 'percentual' ? 'Ex: 10' : 'Ex: 50.00'}
                  required
                />
              </FormGroup>

              {/* DESABILITADO - Taxa de adesão */}
              {/* <FormGroup>
                <Label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={formData.descontoTaxaAdesaoAplica}
                    onChange={(e) => setFormData({ ...formData, descontoTaxaAdesaoAplica: e.target.checked })}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  Aplicar Desconto na Taxa de Adesão
                </Label>
              </FormGroup>

              {formData.descontoTaxaAdesaoAplica && (
                <>
                  <FormGroup>
                    <Label>Tipo de Desconto na Taxa</Label>
                    <Select
                      value={formData.descontoTaxaAdesaoTipo}
                      onChange={(e) => setFormData({ ...formData, descontoTaxaAdesaoTipo: e.target.value })}
                    >
                      <option value="percentual">Percentual (%)</option>
                      <option value="fixo">Valor Fixo (R$)</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Valor do Desconto na Taxa</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.descontoTaxaAdesaoValor}
                      onChange={(e) => setFormData({ ...formData, descontoTaxaAdesaoValor: e.target.value })}
                      placeholder={formData.descontoTaxaAdesaoTipo === 'percentual' ? 'Ex: 50' : 'Ex: 50.00'}
                      required
                    />
                  </FormGroup>
                </>
              )} */}

              <FormGroup>
                <Label>Quantidade Total *</Label>
                <Input
                  type="number"
                  value={formData.quantidadeTotal}
                  onChange={(e) => setFormData({ ...formData, quantidadeTotal: e.target.value })}
                  placeholder="Ex: 100"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Validade (Opcional)</Label>
                <Input
                  type="date"
                  value={formData.validade}
                  onChange={(e) => setFormData({ ...formData, validade: e.target.value })}
                />
              </FormGroup>

              <ModalActions>
                <Button type="button" $variant="danger" onClick={() => setShowModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  Criar Cupom
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}


      {showEditModal && (
        <Modal onClick={() => setShowEditModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Editar Cupom</h3>
              <p>Atualize os dados do cupom de desconto</p>
            </ModalHeader>

            <form onSubmit={handleUpdateCupom}>
              <FormGroup>
                <Label>Código do Cupom</Label>
                <Input
                  type="text"
                  value={formData.codigo}
                  disabled
                  style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                />
              </FormGroup>

              <FormGroup>
                <Label>Vendedor *</Label>
                <Select
                  value={formData.vendedorId}
                  onChange={(e) => setFormData({ ...formData, vendedorId: e.target.value })}
                  required
                >
                  <option value="">Selecione um vendedor</option>
                  {vendedores.map((vendedor) => (
                    <option key={vendedor.id} value={vendedor.id}>
                      {vendedor.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Tipo de Benefício *</Label>
                <Select
                  value={formData.beneficioTipo}
                  onChange={(e) => setFormData({ ...formData, beneficioTipo: e.target.value })}
                  required
                >
                  <option value="percentual">Percentual (%)</option>
                  <option value="fixo">Valor Fixo (R$)</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Valor do Benefício *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.beneficioValor}
                  onChange={(e) => setFormData({ ...formData, beneficioValor: e.target.value })}
                  placeholder={formData.beneficioTipo === 'percentual' ? 'Ex: 10' : 'Ex: 50.00'}
                  required
                />
              </FormGroup>

              {/* DESABILITADO - Taxa de adesão */}
              {/* <FormGroup>
                <Label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={formData.descontoTaxaAdesaoAplica}
                    onChange={(e) => setFormData({ ...formData, descontoTaxaAdesaoAplica: e.target.checked })}
                    style={{ width: 'auto', margin: 0 }}
                  />
                  Aplicar Desconto na Taxa de Adesão
                </Label>
              </FormGroup>

              {formData.descontoTaxaAdesaoAplica && (
                <>
                  <FormGroup>
                    <Label>Tipo de Desconto na Taxa</Label>
                    <Select
                      value={formData.descontoTaxaAdesaoTipo}
                      onChange={(e) => setFormData({ ...formData, descontoTaxaAdesaoTipo: e.target.value })}
                    >
                      <option value="percentual">Percentual (%)</option>
                      <option value="fixo">Valor Fixo (R$)</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Valor do Desconto na Taxa</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.descontoTaxaAdesaoValor}
                      onChange={(e) => setFormData({ ...formData, descontoTaxaAdesaoValor: e.target.value })}
                      placeholder={formData.descontoTaxaAdesaoTipo === 'percentual' ? 'Ex: 50' : 'Ex: 50.00'}
                      required
                    />
                  </FormGroup>
                </>
              )} */}

              <FormGroup>
                <Label>Quantidade Total *</Label>
                <Input
                  type="number"
                  value={formData.quantidadeTotal}
                  onChange={(e) => setFormData({ ...formData, quantidadeTotal: e.target.value })}
                  placeholder="Ex: 100"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Validade (Opcional)</Label>
                <Input
                  type="date"
                  value={formData.validade}
                  onChange={(e) => setFormData({ ...formData, validade: e.target.value })}
                />
              </FormGroup>

              <ModalActions>
                <Button type="button" $variant="danger" onClick={() => setShowEditModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  Salvar Alterações
                </Button>
              </ModalActions>
            </form>
          </ModalContent>
        </Modal>
      )}

      {showDeleteModal && selectedCupom && (
        <Modal onClick={() => setShowDeleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Confirmar Exclusão</h3>
              <p>Esta ação não poderá ser desfeita</p>
            </ModalHeader>

            <div style={{ marginBottom: '24px' }}>
              <p style={{ fontSize: '16px', marginBottom: '12px' }}>
                Tem certeza que deseja excluir o cupom <strong>{selectedCupom.codigo}</strong>?
              </p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                Vendedor: {selectedCupom.vendedorNome}<br />
                Quantidade usada: {selectedCupom.quantidadeUsada} / {selectedCupom.quantidadeTotal}
              </p>
            </div>

            <ModalActions>
              <Button type="button" $variant="secondary" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button type="button" $variant="danger" onClick={confirmDeleteCupom}>
                Sim, Excluir
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}

      {/* Modal de Detalhes do Cadastro */}
      {showCadastroModal && selectedCadastro && (
        <Modal onClick={() => setShowCadastroModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <ModalHeader>
              <h3><ClipboardListIcon />Detalhes do Cadastro</h3>
              <p>Informações completas da venda</p>
            </ModalHeader>

            <div style={{ padding: '24px' }}>
              {/* Informações Gerais */}
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{ marginBottom: '16px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
                  <BarChart3Icon />Informações Gerais
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>ID do Cadastro</label>
                    <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600' }}>{selectedCadastro.id}</p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Status</label>
                    <p style={{ margin: '4px 0' }}>
                      <Badge status={selectedCadastro.status === 'CONFIRMADO' ? 'ativo' : selectedCadastro.status === 'AGUARDANDO_CONFIRMACAO' ? 'warning' : 'inativo'}>
                        {selectedCadastro.status || 'N/A'}
                      </Badge>
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Data do Cadastro</label>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      {selectedCadastro.dataCadastro || selectedCadastro.createdAt 
                        ? new Date(selectedCadastro.dataCadastro || selectedCadastro.createdAt).toLocaleString('pt-BR')
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Plano</label>
                    <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600' }}>{selectedCadastro.planoNome || selectedCadastro.plano?.nome || 'N/A'}</p>
                  </div>
                  {selectedCadastro.planoKey && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Chave do Plano</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>{selectedCadastro.planoKey || selectedCadastro.plano?.key || 'N/A'}</p>
                    </div>
                  )}
                  {selectedCadastro.planoId && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>ID do Plano</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>{selectedCadastro.planoId || selectedCadastro.plano?.idPlano || 'N/A'}</p>
                    </div>
                  )}
                  {selectedCadastro.origem && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Origem</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>{selectedCadastro.origem}</p>
                    </div>
                  )}
                  {selectedCadastro.dataVencimento && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Data de Vencimento</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>Dia {selectedCadastro.dataVencimento} de cada mês</p>
                    </div>
                  )}
                  {selectedCadastro.receberFisico !== undefined && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Receber Físico</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedCadastro.receberFisico ? '✓ Sim' : '✗ Não'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Dados do Titular */}
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{ marginBottom: '16px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
                  <UserIcon />Dados do Titular
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Nome Completo</label>
                    <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600' }}>
                      {selectedCadastro.titularNome || selectedCadastro.titular?.nome || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>CPF</label>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      {selectedCadastro.titularCPF || selectedCadastro.titular?.cpf || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Data de Nascimento</label>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      {selectedCadastro.titular?.dataNascimento 
                        ? new Date(selectedCadastro.titular.dataNascimento).toLocaleDateString('pt-BR')
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Idade</label>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      {selectedCadastro.titular?.idade ? `${selectedCadastro.titular.idade} anos` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Sexo</label>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      {selectedCadastro.titular?.sexo === 'M' ? 'Masculino' : selectedCadastro.titular?.sexo === 'F' ? 'Feminino' : selectedCadastro.titular?.sexo || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Email</label>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      {selectedCadastro.titularEmail || selectedCadastro.titular?.email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Telefone</label>
                    <p style={{ margin: '4px 0', fontSize: '14px' }}>
                      {selectedCadastro.titularTelefone || selectedCadastro.titular?.telefone || 'N/A'}
                      {selectedCadastro.titular?.isWhatsApp && (
                        <span style={{ marginLeft: '8px', fontSize: '12px', color: '#10b981', fontWeight: '600' }}>✓ WhatsApp</span>
                      )}
                    </p>
                  </div>
                  {selectedCadastro.titular?.preco != null && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Preço do Titular</label>
                      <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600', color: '#10b981' }}>
                        R$ {parseFloat(selectedCadastro.titular.preco || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                  {selectedCadastro.titular?.endereco && (
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Endereço Completo</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedCadastro.titular.endereco.completo || 
                         `${selectedCadastro.titular.endereco.rua || ''}, ${selectedCadastro.titular.endereco.numero || ''}${selectedCadastro.titular.endereco.complemento ? ' - ' + selectedCadastro.titular.endereco.complemento : ''} - ${selectedCadastro.titular.endereco.bairro || ''}, ${selectedCadastro.titular.endereco.cidade || ''} - ${selectedCadastro.titular.endereco.estado || ''} | CEP: ${selectedCadastro.titular.endereco.cep || ''}`}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {selectedCadastro.responsavelFinanceiro && (
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{ marginBottom: '16px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
                    <CreditCardIcon />Responsável Financeiro
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Nome</label>
                      <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600' }}>{selectedCadastro.responsavelFinanceiro.nome || 'N/A'}</p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>CPF</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>{selectedCadastro.responsavelFinanceiro.cpf || 'N/A'}</p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>E-mail</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>{selectedCadastro.responsavelFinanceiro.email || 'N/A'}</p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Celular</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>{selectedCadastro.responsavelFinanceiro.telefoneCelular || 'N/A'}</p>
                    </div>
                    {selectedCadastro.responsavelFinanceiro.telefoneResidencial && (
                      <div>
                        <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Telefone Residencial</label>
                        <p style={{ margin: '4px 0', fontSize: '14px' }}>{selectedCadastro.responsavelFinanceiro.telefoneResidencial}</p>
                      </div>
                    )}
                    {selectedCadastro.responsavelFinanceiro.endereco && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Endereço</label>
                        <p style={{ margin: '4px 0', fontSize: '14px' }}>
                          {selectedCadastro.responsavelFinanceiro.endereco.completo ||
                            `${selectedCadastro.responsavelFinanceiro.endereco.rua || ''}, ${selectedCadastro.responsavelFinanceiro.endereco.numero || ''}${selectedCadastro.responsavelFinanceiro.endereco.complemento ? ' - ' + selectedCadastro.responsavelFinanceiro.endereco.complemento : ''} - ${selectedCadastro.responsavelFinanceiro.endereco.bairro || ''}, ${selectedCadastro.responsavelFinanceiro.endereco.cidade || ''} - ${selectedCadastro.responsavelFinanceiro.endereco.estado || ''} | CEP: ${selectedCadastro.responsavelFinanceiro.endereco.cep || ''}`}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Dependentes */}
              {selectedCadastro.dependentes && selectedCadastro.dependentes.length > 0 && (
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{ marginBottom: '16px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
                    <LuUsers size={18} style={iconStyle} />Dependentes ({selectedCadastro.dependentes.length})
                  </h4>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {selectedCadastro.dependentes.map((dep, idx) => (
                      <div key={idx} style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                          <div>
                            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Nome</label>
                            <p style={{ margin: '4px 0', fontSize: '13px', fontWeight: '600' }}>{dep.nome || 'N/A'}</p>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>CPF</label>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>{dep.cpf || 'N/A'}</p>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Data de Nascimento</label>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>
                              {dep.dataNascimento ? new Date(dep.dataNascimento).toLocaleDateString('pt-BR') : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Idade</label>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>{dep.idade ? `${dep.idade} anos` : 'N/A'}</p>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Sexo</label>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>
                              {dep.sexo === 'M' ? 'Masculino' : dep.sexo === 'F' ? 'Feminino' : dep.sexo || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Parentesco</label>
                            <p style={{ margin: '4px 0', fontSize: '13px' }}>
                              {(() => {
                                if (!dep.parentesco) return 'N/A';
                                const parentescoId = String(dep.parentesco);
                                const parentesco = parentescos.find(p => String(p.id) === parentescoId);
                                return parentesco ? parentesco.nome : dep.parentesco;
                              })()}
                            </p>
                          </div>
                          {dep.preco && (
                            <div>
                              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Preço</label>
                              <p style={{ margin: '4px 0', fontSize: '13px', fontWeight: '600', color: '#10b981' }}>
                                R$ {parseFloat(dep.preco || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pets */}
              {selectedCadastro.pets && selectedCadastro.pets.length > 0 && (
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{ marginBottom: '16px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
                    <PawIcon />Pets ({selectedCadastro.pets.length})
                  </h4>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {selectedCadastro.pets.map((pet, idx) => {
                      const especieMap = {
                        1: 'Humano',
                        2: 'Cachorro',
                        3: 'Gato',
                        6: 'Pássaro',
                        11: 'Exótico'
                      };
                      return (
                        <div key={idx} style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                            <div>
                              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Nome</label>
                              <p style={{ margin: '4px 0', fontSize: '13px', fontWeight: '600' }}>{pet.nome || 'N/A'}</p>
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Espécie</label>
                              <p style={{ margin: '4px 0', fontSize: '13px' }}>
                                {pet.idEspecie ? especieMap[pet.idEspecie] || `Espécie ${pet.idEspecie}` : 'N/A'}
                              </p>
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Raça</label>
                              <p style={{ margin: '4px 0', fontSize: '13px' }}>{pet.raca || 'N/A'}</p>
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Idade</label>
                              <p style={{ margin: '4px 0', fontSize: '13px' }}>{pet.idade ? `${pet.idade} anos` : 'N/A'}</p>
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Peso</label>
                              <p style={{ margin: '4px 0', fontSize: '13px' }}>{pet.peso ? `${pet.peso} kg` : 'N/A'}</p>
                            </div>
                            {pet.preco && (
                              <div>
                                <label style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>Preço</label>
                                <p style={{ margin: '4px 0', fontSize: '13px', fontWeight: '600', color: '#10b981' }}>
                                  R$ {parseFloat(pet.preco || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Valores */}
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{ marginBottom: '16px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
                  <LuDollarSign size={18} style={iconStyle} />Valores
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Valor Total Mensal</label>
                    <p style={{ margin: '4px 0', fontSize: '16px', fontWeight: '700', color: '#10b981' }}>
                      R$ {parseFloat(selectedCadastro.valorTotal || selectedCadastro.valores?.totalMensal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Valor Original</label>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                      R$ {parseFloat(selectedCadastro.valorOriginal || selectedCadastro.valores?.totalOriginal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  {selectedCadastro.valorFrete > 0 && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Frete</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        R$ {parseFloat(selectedCadastro.valorFrete || selectedCadastro.valores?.valorFrete || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                  {selectedCadastro.descontoAplicado > 0 && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Desconto Aplicado</label>
                      <p style={{ margin: '4px 0', fontSize: '14px', color: '#f59e0b', fontWeight: '600' }}>
                        R$ {parseFloat(selectedCadastro.descontoAplicado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                  {selectedCadastro.valores?.primeiraParcela && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Primeira Parcela</label>
                      <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600', color: '#3b82f6' }}>
                        R$ {parseFloat(selectedCadastro.valores.primeiraParcela || selectedCadastro.primeiraParcela || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                  {selectedCadastro.valores?.demaisParcelas && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Demais Parcelas</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        R$ {parseFloat(selectedCadastro.valores.demaisParcelas || selectedCadastro.demaisParcelas || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                  {selectedCadastro.valores?.totalComFrete && (
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Total com Frete</label>
                      <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600' }}>
                        R$ {parseFloat(selectedCadastro.valores.totalComFrete || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Origem da Campanha (UTM) */}
              {(selectedCadastro.utm_source || selectedCadastro.utm_campaign || selectedCadastro.utm_medium || selectedCadastro.utm_provider) && (
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{ marginBottom: '16px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
                    <MegaphoneIcon />Origem da Campanha (UTM)
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>UTM Source</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedCadastro.utm_source || '-'}
                      </p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>UTM Campaign</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedCadastro.utm_campaign || '-'}
                      </p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>UTM Medium</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedCadastro.utm_medium || '-'}
                      </p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>UTM Provider</label>
                      <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600' }}>
                        {selectedCadastro.utm_provider || '-'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cupom */}
              {(selectedCadastro.cupomCodigo || selectedCadastro.cupom) && (
                <div style={{ marginBottom: '32px' }}>
                  <h4 style={{ marginBottom: '16px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
                    <TicketIcon />Cupom Utilizado
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Código</label>
                      <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600' }}>
                        <Badge status="ativo">{selectedCadastro.cupomCodigo || selectedCadastro.cupom?.codigo || 'N/A'}</Badge>
                      </p>
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Vendedor</label>
                      <p style={{ margin: '4px 0', fontSize: '14px' }}>
                        {selectedCadastro.vendedorNome || selectedCadastro.cupom?.vendedorNome || 'N/A'}
                      </p>
                    </div>
                    {selectedCadastro.cupom?.beneficio && (
                      <>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Tipo de Benefício</label>
                          <p style={{ margin: '4px 0', fontSize: '14px' }}>
                            {selectedCadastro.cupom.beneficio.tipo === 'percentual' ? 'Percentual' : 'Valor Fixo'}
                          </p>
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Valor do Benefício</label>
                          <p style={{ margin: '4px 0', fontSize: '14px', fontWeight: '600' }}>
                            {selectedCadastro.cupom.beneficio.tipo === 'percentual' 
                              ? `${selectedCadastro.cupom.beneficio.valor}%`
                              : `R$ ${parseFloat(selectedCadastro.cupom.beneficio.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Totais */}
              <div style={{ marginBottom: '32px', padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '2px solid #10b981' }}>
                <h4 style={{ marginBottom: '16px', color: '#1f2937' }}><BarChart3Icon />Resumo</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Total de Vidas</label>
                    <p style={{ margin: '4px 0', fontSize: '18px', fontWeight: '700', color: '#3b82f6' }}>
                      {selectedCadastro.pessoasTotal || (1 + (selectedCadastro.dependentes?.length || 0))}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Total de Pets</label>
                    <p style={{ margin: '4px 0', fontSize: '18px', fontWeight: '700', color: '#14b8a6' }}>
                      {selectedCadastro.petsTotal || selectedCadastro.pets?.length || 0}
                    </p>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase' }}>Valor Final</label>
                    <p style={{ margin: '4px 0', fontSize: '20px', fontWeight: '700', color: '#10b981' }}>
                      R$ {parseFloat(selectedCadastro.valorTotal || selectedCadastro.valores?.totalMensal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <ModalActions>
              <Button type="button" onClick={() => setShowCadastroModal(false)}>
                Fechar
              </Button>
            </ModalActions>
          </ModalContent>
        </Modal>
      )}

    </PageContainer>
  );
};

export default AdminCupons;

